import type * as React from 'react'
import { AlertCircle, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

export type StepState = 'complete' | 'current' | 'upcoming' | 'invalid'

export interface StepperStep {
  id: string
  title: string
  optional?: boolean
}

const stateLabels: Record<StepState, string> = {
  complete: 'done',
  current: 'current step',
  upcoming: 'not started',
  invalid: 'needs attention',
}

function StepMarker({ index, state }: { index: number; state: StepState }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium tabular-nums',
        state === 'complete' && 'border-primary bg-primary text-primary-foreground',
        state === 'current' && 'border-primary text-primary',
        state === 'upcoming' && 'text-muted-foreground',
        state === 'invalid' && 'border-destructive text-destructive',
      )}
    >
      {state === 'complete' ? (
        <Check className="size-3.5" />
      ) : state === 'invalid' ? (
        <AlertCircle className="size-3.5" />
      ) : (
        index + 1
      )}
    </span>
  )
}

/**
 * Where someone is in a multi-step form: a rail of steps on wide screens, and
 * "Step 2 of 5" with a segmented bar on phones. Each step in the rail is a
 * button; whoever owns the form decides whether moving forward is allowed.
 */
function Stepper({
  steps,
  currentIndex,
  stateOf,
  onSelect,
  label = 'Form steps',
  className,
  ...props
}: Omit<React.ComponentProps<'nav'>, 'onSelect'> & {
  steps: readonly StepperStep[]
  currentIndex: number
  stateOf: (index: number) => StepState
  onSelect: (index: number) => void
  label?: string
  className?: string
}) {
  const current = steps[currentIndex]

  return (
    <nav {...props} aria-label={label} data-slot="stepper" className={cn('min-w-0', className)}>
      <div className="lg:hidden">
        <p className="flex min-w-0 items-baseline gap-2 text-sm">
          <span className="shrink-0 text-muted-foreground">
            Step {currentIndex + 1} of {steps.length}
          </span>
          <span className="truncate font-medium">{current?.title}</span>
        </p>
        <div className="mt-2 flex gap-1" aria-hidden="true">
          {steps.map((step, index) => (
            <span
              key={step.id}
              className={cn(
                'h-1.5 flex-1 rounded-full bg-muted',
                index <= currentIndex && 'bg-primary',
                stateOf(index) === 'invalid' && 'bg-destructive',
              )}
            />
          ))}
        </div>
      </div>

      <ol className="hidden lg:flex lg:flex-col lg:gap-1">
        {steps.map((step, index) => {
          const state = stateOf(index)
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => onSelect(index)}
                aria-current={index === currentIndex ? 'step' : undefined}
                className={cn(
                  'flex w-full items-start gap-3 rounded-md px-2 py-2 text-left text-sm outline-none transition-colors',
                  'hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  index === currentIndex && 'bg-muted',
                )}
              >
                <StepMarker index={index} state={state} />
                <span className="min-w-0 pt-0.5">
                  <span
                    className={cn(
                      'block truncate',
                      index === currentIndex
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground',
                      state === 'invalid' && 'text-destructive',
                    )}
                  >
                    {step.title}
                  </span>
                  {step.optional && (
                    <span className="block text-xs text-muted-foreground">Optional</span>
                  )}
                  <span className="sr-only">, {stateLabels[state]}</span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export { Stepper }
